import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Department} from '../../types/types';
import {
  Branch,
  GroupedDepartments,
} from '../../types/client-management/departmentTypes';
import {IProjectTableData} from '../../components/client-details/project-data-table/types';
import {AurDataGridFilterState} from '../../components/common/aur-data-grid-pro/useServerSideDataGrid';

interface DepartmentState {
  clientId: string;
  departmentList: Department[];
  groupedDepartmentList: GroupedDepartments[];
  branchList: Branch[];
  departmentAccordionCollapsed: boolean;
  branchAccordionCollapsed: boolean;
  drawerAccordionDisable: boolean;
  triggerResize: boolean;
  drawerAccordionMaxHeight: number;
  isFormStateChanged: boolean;
  onEditMode: boolean;
  onCreateMode: boolean;
  selectedProjectId: string;
  selectedProjectIndex: number;
  allProject: IProjectTableData[];
  projectTableFilterState?: AurDataGridFilterState;
}

const initialState: DepartmentState = {
  clientId: '',
  departmentList: [],
  branchList: [],
  groupedDepartmentList: [],
  departmentAccordionCollapsed: false,
  branchAccordionCollapsed: false,
  drawerAccordionDisable: false,
  drawerAccordionMaxHeight: 0,
  triggerResize: false,
  isFormStateChanged: false,
  onEditMode: false,
  onCreateMode: false,
  selectedProjectId: '',
  allProject: [],
  projectTableFilterState: {
    searchState: {
      items: [],
    },
    sortState: [],
    paginationState: {page: 1, pageSize: 1},
  },
  selectedProjectIndex: -1,
};

const departmentSlice = createSlice({
  name: 'clientDetails',
  initialState,
  reducers: {
    setClientId(state, action: PayloadAction<string>) {
      state.clientId = action.payload;
    },
    setDepartments(state, action: PayloadAction<Department[]>) {
      state.departmentList = action.payload.map((department: Department) => ({
        ...department,
        expanded: action.payload.length <= 8,
      }));
    },
    setGroupedDepartments(state, action: PayloadAction<GroupedDepartments[]>) {
      state.groupedDepartmentList = action.payload.map(
        (department: GroupedDepartments) => ({
          ...department,
        })
      );
    },
    updateGroupedDepartments(state, action: PayloadAction<GroupedDepartments>) {
      const index = state.groupedDepartmentList.findIndex(
        (department) =>
          department.clientBranchId === action.payload.clientBranchId
      );

      if (index !== -1) {
        state.groupedDepartmentList[index] = action.payload;
      }
    },
    updateDepartment(
      state,
      action: PayloadAction<{
        clientBranchId: string;
        clientDepartmentId: string;
        updatedDepartment: Department;
      }>
    ) {
      const {clientBranchId, clientDepartmentId, updatedDepartment} =
        action.payload;
      const branchIndex = state.groupedDepartmentList.findIndex(
        (branch) => branch.clientBranchId === clientBranchId
      );
      state.groupedDepartmentList[branchIndex].departments.findIndex(
        (department) => department.clientDepartmentId === clientDepartmentId
      );

      if (branchIndex !== -1) {
        const departmenIndex = state.groupedDepartmentList[
          branchIndex
        ].departments.findIndex(
          (department) => department.clientDepartmentId === clientDepartmentId
        );
        if (departmenIndex !== -1) {
          state.groupedDepartmentList[branchIndex].departments[departmenIndex] =
            updatedDepartment;
        }
      }
    },
    addDepartment(state, action: PayloadAction<Department>) {
      state.departmentList.push(action.payload);
      state.departmentList = state.departmentList
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((department: Department) => ({
          ...department,
          expanded: state.departmentList.length <= 8,
        }));
    },

    removeDepartment(
      state,
      action: PayloadAction<{
        clientBranchId: string;
        clientDepartmentId: string;
      }>
    ) {
      const {clientBranchId, clientDepartmentId} = action.payload;
      const branchIndex = state.groupedDepartmentList.findIndex(
        (branch) => branch.clientBranchId === clientBranchId
      );
      state.groupedDepartmentList[branchIndex].departments =
        state.groupedDepartmentList[branchIndex].departments.filter(
          (department) => department.clientDepartmentId !== clientDepartmentId
        );
    },
    setDepartmentAccordionCollapsed(state, action: PayloadAction<boolean>) {
      state.departmentAccordionCollapsed = action.payload;
    },

    setBranches(state, action: PayloadAction<Branch[]>) {
      state.branchList = action.payload;
    },

    setBranchAccordionCollapsed(state, action: PayloadAction<boolean>) {
      state.branchAccordionCollapsed = action.payload;
    },
    setDrawerAccordionDisable(state, action: PayloadAction<boolean>) {
      state.drawerAccordionDisable = action.payload;
    },
    setDrawerAccordionMaxHeight(state, action: PayloadAction<number>) {
      state.drawerAccordionMaxHeight = action.payload;
    },
    setEditMode(state, action: PayloadAction<boolean>) {
      state.onEditMode = action.payload;
    },
    setCreateMode(state, action: PayloadAction<boolean>) {
      state.onCreateMode = action.payload;
    },
    triggerResize(state) {
      state.triggerResize = !state.triggerResize;
    },
    clearDrawerState(state) {
      (state.departmentList = []),
        (state.drawerAccordionDisable = false),
        (state.drawerAccordionMaxHeight = 0),
        (state.onEditMode = false);
    },
    setClientDetailsFormState(state, action: PayloadAction<boolean>) {
      state.isFormStateChanged = action.payload;
    },
    updateSelectedProjectId(state, action: PayloadAction<string>) {
      state.selectedProjectId = action.payload;
    },
    updateSelectedProjectIndex(state, action: PayloadAction<number>) {
      state.selectedProjectIndex = action.payload;
    },
    setAllProject(state, action: PayloadAction<IProjectTableData[]>) {
      state.allProject = action.payload;
    },
    setProjectTableFilterState(
      state,
      action: PayloadAction<AurDataGridFilterState | undefined>
    ) {
      state.projectTableFilterState = action.payload;
    },
  },
});

export const {
  removeDepartment,
  setGroupedDepartments,
  updateGroupedDepartments,
  setDepartmentAccordionCollapsed,
  setDrawerAccordionMaxHeight,
  setDrawerAccordionDisable,
  triggerResize,
  clearDrawerState,
  setBranches,
  setClientDetailsFormState,
  setBranchAccordionCollapsed,
  setEditMode,
  updateDepartment,
  setCreateMode,
  updateSelectedProjectId,
  setProjectTableFilterState,
  updateSelectedProjectIndex,
} = departmentSlice.actions;

export default departmentSlice.reducer;
